import { apiFetch } from "@common/lib/api";

/**
 * EN-1248: client for the AVImark mapping editor. The response shapes here are the contract the
 * API-Server work builds to (spec: specs/2026-10-06-en-1248-avimark-mapping-editor-spec.md, B1 and B3).
 */

export interface AvimarkDocumentField {
    id: string;
    name: string;
}

export interface AvimarkDocumentSection {
    id: string;
    name: string;
    /** Only HappySOAP's shared Objective section and dental quadrants keep abnormal flags. */
    abnormalCapable: boolean;
    fields: AvimarkDocumentField[];
}

export interface AvimarkBodySystem {
    /** integrations_fields row id: the mapping target. */
    id: string;
    code: string;
    name: string;
    sortIndex: number;
    pimsNormalText: string | null;
    pimsNotExaminedText: string | null;
    /** EN-1250: the code left the clinic's AVImark (soft-removed). */
    noLongerInAvimark: boolean;
}

export interface AvimarkNoteTarget {
    id: string;
    code: string;
    name: string;
}

export interface AvimarkMappingItem {
    id: string;
    documentExportId: string | null;
    stableFieldId: string;
    integrationFieldDbId: string;
    sortIndex: number;
    isActive: boolean;
}

export interface AvimarkTargetGroup {
    id: string;
    integrationFieldId: string;
    integrationFieldName: string;
    sortIndex: number;
    mappings: AvimarkMappingItem[];
}

export interface AvimarkEditorBlock {
    /** EN-1248 workshop addition (review M5): the document's display name, for the title and copy.
     *  The read sends null when the name lookup fails; treat it like absent. */
    documentName?: string | null;
    documentExportId: string | null;
    isActive: boolean;
    leaveOutStableFieldIds: string[];
    confirmedAt: string | null;
    documentFields: AvimarkDocumentSection[];
    canFlagAbnormal: boolean;
    bodySystems: AvimarkBodySystem[];
    noteTargets: AvimarkNoteTarget[];
    /** stableFieldId -> name, for saved matches whose field is no longer in the document. */
    deletedFieldNames: Record<string, string>;
    isMonitoring: boolean;
}

export interface AvimarkEditorData {
    integrationFields: AvimarkTargetGroup[];
    avimark: AvimarkEditorBlock;
}

export interface AvimarkPair {
    stableFieldId: string;
    integrationFieldDbId: string;
}

export type AvimarkDestination = { kind: "matched"; targets: string[] } | { kind: "exam-notes" } | { kind: "left-out" };

export type VetWordingFallback = "too-long" | "not-discussed" | "empty";

export interface PreviewRow {
    code: string;
    name: string;
    ticked: boolean;
    text: string;
    textKind: "abnormal" | "vet-normal" | "clinic-normal" | "clinic-not-examined";
    vetWordingFallback?: VetWordingFallback;
    genericWording: boolean;
}

export interface DisplayPart {
    id: string;
    name?: string;
    value?: string | string[];
    isNormal?: boolean;
    inTranscript?: boolean;
    fields?: DisplayPart[];
}

export interface AvimarkPreviewResponse {
    resolution: {
        byStableFieldId: Record<string, AvimarkDestination>;
        counts: { matchedCount: number; toExamNotesCount: number; leftOutCount: number };
    };
    preview: {
        sample: {
            patientName: string;
            species: string;
            sex: string;
            age: string;
            breed: string;
            weight: string;
            visitDate: string;
        };
        sampleDocument: DisplayPart[];
        notes: { complaint?: string; exam?: string; assessment?: string; plan?: string; clientInstructions?: string };
        rows: PreviewRow[];
        settings: { tickRule: "mentioned" | "all-mapped"; normalWording: "clinic" | "vet" };
    };
}

/** EN-1237's readiness codes; the export and the hub list use the same function server-side. */
export type AvimarkReadinessCode =
    | "avimark_export_disabled"
    | "avimark_setup_incomplete"
    | "avimark_document_export_off"
    | "avimark_document_not_ready"
    | "avimark_no_mappings";

/** One row of GET integration/avimark/documents (API-Server avimarkExportSettingsController.ts). */
export interface AvimarkDocumentSummary {
    documentTypeId: string;
    documentExportId: string | null;
    isActive: boolean;
    confirmedAt: string | null;
    matchedCount: number | null;
    leftOutCount: number | null;
    toExamNotesCount: number | null;
    readiness: { ok: true } | { ok: false; code: AvimarkReadinessCode; error: string };
}

export interface AvimarkExportSettings {
    tickRule: "mentioned" | "all-mapped";
    normalWording: "clinic" | "vet";
}

export type AvimarkAccountStatus = "yes" | "no" | "error";

const statusOf = (e: unknown): number | undefined => (e as { response?: { status?: number } })?.response?.status;

/** null when the account is not HappyBridge AVImark (the read has no `avimark` block). */
export async function fetchAvimarkEditorData(documentTypeId: string): Promise<AvimarkEditorData | null> {
    const data = await apiFetch<{ integrationFields: AvimarkTargetGroup[]; avimark?: AvimarkEditorBlock }>(
        "integration/export-field-mappings",
        { method: "GET", searchParams: { documentTypeId, include: "editor" } },
    );
    if (!data?.avimark) return null;
    return { integrationFields: data.integrationFields ?? [], avimark: data.avimark };
}

export async function fetchAvimarkPreview(
    req: { documentTypeId: string; mappings: AvimarkPair[]; leaveOutStableFieldIds: string[] },
    signal?: AbortSignal,
): Promise<AvimarkPreviewResponse> {
    const data = await apiFetch<AvimarkPreviewResponse>("integration/avimark/preview", {
        method: "POST",
        json: req,
        signal,
    });
    if (!data) throw new Error("Empty AVImark preview response");
    return data;
}

/** 200 = HappyBridge AVImark account, 404 = not one, anything else = we could not tell. */
export async function fetchAvimarkAccountStatus(): Promise<AvimarkAccountStatus> {
    try {
        await apiFetch("integration/avimark/settings", { method: "GET", skipSentryOn404: true });
        return "yes";
    } catch (e) {
        return statusOf(e) === 404 ? "no" : "error";
    }
}

/** Creates the document's export row. Never sends isActive, so an existing switch is untouched. */
export async function createAvimarkDocumentExport(documentTypeId: string): Promise<string> {
    const data = await apiFetch<{ documentExportId: string }>("integration/export/create", {
        method: "POST",
        json: { documentTypeId },
    });
    if (!data?.documentExportId) throw new Error("No documentExportId returned");
    return data.documentExportId;
}

export async function saveAvimarkMatches(req: {
    documentExportId: string;
    mappings: AvimarkPair[];
    leaveOutStableFieldIds: string[];
}): Promise<void> {
    await apiFetch("integration/export-field-mappings", {
        method: "POST",
        json: {
            documentExportId: req.documentExportId,
            mappings: req.mappings.map((m) => ({
                documentExportId: req.documentExportId,
                stableFieldId: m.stableFieldId,
                integrationFieldDbId: m.integrationFieldDbId,
            })),
            leaveOutStableFieldIds: req.leaveOutStableFieldIds,
            fromAvimarkEditor: true,
        },
    });
}

/** The hub list endpoint takes at most 50 ids per call. */
export async function fetchAvimarkDocumentSummaries(
    documentTypeIds: string[],
    opts?: { timeout?: number; retry?: number; silent?: boolean },
): Promise<AvimarkDocumentSummary[]> {
    const out: AvimarkDocumentSummary[] = [];
    for (let i = 0; i < documentTypeIds.length; i += 50) {
        const ids = documentTypeIds.slice(i, i + 50);
        const data = await apiFetch<{ documents: AvimarkDocumentSummary[] }>("integration/avimark/documents", {
            method: "GET",
            searchParams: { documentTypeIds: ids.join(",") },
            ...(opts?.timeout !== undefined ? { timeout: opts.timeout } : {}),
            ...(opts?.retry !== undefined ? { retry: opts.retry } : {}),
            ...(opts?.silent ? { silent: true } : {}),
        });
        out.push(...(data?.documents ?? []));
    }
    return out;
}

/** EN-1249: the two per-account body-system settings shown on the hub. */
export async function fetchAvimarkSettings(): Promise<AvimarkExportSettings> {
    const data = await apiFetch<AvimarkExportSettings>("integration/avimark/settings", { method: "GET" });
    if (!data) throw new Error("Empty AVImark settings response");
    return data;
}

export async function saveAvimarkSettings(s: AvimarkExportSettings): Promise<AvimarkExportSettings> {
    const data = await apiFetch<AvimarkExportSettings>("integration/avimark/settings", { method: "PUT", json: s });
    if (!data) throw new Error("Empty response from integration/avimark/settings");
    return data;
}

/** EN-1249: the hub's per-document switch. Sends isActive only, so matches and avimark_confirmed_at are untouched. */
export async function setAvimarkDocumentExportActive(documentTypeId: string, isActive: boolean): Promise<void> {
    await apiFetch("integration/export/create", { method: "POST", json: { documentTypeId, isActive } });
}
